/**
 * REST API between the web client and the Worker. JSON in, JSON out; errors are `ApiError` with an HTTP status.
 * All routes require the session cookie unless marked PUBLIC or DAEMON.
 * Non-GET browser requests must send `Content-Type: application/json` (CSRF guard in src/worker/index.ts).
 */
import type { RuleInput, RuleView } from "./rules";
import type {
  AgentSettings,
  ApprovalView,
  AvatarStyle,
  Brief,
  ChatMessage,
  ChatFile,
  ConnectionsView,
  CreateMissionInput,
  Dashboard,
  IcloudConnectInput,
  IcloudView,
  MachinePolicy,
  MachineSize,
  MachineSizeName,
  MachineView,
  MeView,
  MemoryExplanation,
  MemorySleepReport,
  MemoryForgetReason,
  MemoryItemView,
  MemoryKind,
  MemorySensitivity,
  MissionRunView,
  MissionView,
  ProfileBlockView,
  RecallResult,
  RunDetail,
  TurnView,
  UsageView,
} from "./contracts";
import type { BotThreadView, BotView, CreateBotInput } from "./bots";
import { ICLOUD_SERVICES } from "./icloud";
import type { WatchCreateInput, WatchesView, WatchPatchInput } from "./watches";
import type { FieldKind, FormKind, VaultItemView, VaultUseView } from "./vault";

/** What « Mes agents » edits on one agent (its identity and way of writing). */
export type BotPatch = Partial<Pick<AgentSettings, "agentName" | "agentEmoji" | "persona" | "personaNotes" | "accentColor" | "emojiLevel" | "messageLength" | "formality" | "agentGender" | "character">>;

export interface Api {
  // ── Auth (PUBLIC) ──
  /** New accounts need an invite code in invite mode (no code is sent otherwise); SMS codes only go to phones already linked. */
  "POST /api/auth/start": { body: { identifier: string; inviteCode?: string }; res: { ok: true; channel: "email" | "sms"; masked: string } };
  "POST /api/auth/verify": { body: { identifier: string; code: string; inviteCode?: string }; res: { me: MeView } };
  "POST /api/auth/logout": { body: Record<string, never>; res: { ok: true } };
  /** Public site (/decouvrir): be told when a place opens. Same answer whether the address was already listed. */
  "POST /api/waitlist": { body: { email: string; language?: string }; res: { ok: true } };

  // ── Me, onboarding, settings ──
  "GET /api/me": { res: MeView };
  /** Deletes the account and everything attached to it (agent data, memory, machines, files, links). Irreversible. */
  "DELETE /api/me": { body: { confirm: "SUPPRIMER" }; res: { ok: true } };
  "PATCH /api/me": { body: { name?: string; timezone?: string }; res: MeView };
  "POST /api/onboarding": {
    body: {
      userName: string;
      agentName: string;
      agentEmoji?: string;
      persona: AgentSettings["persona"];
      personaNotes?: string;
      timezone: string;
      briefTime: string;
      primaryChannel?: AgentSettings["primaryChannel"];
    };
    res: { me: MeView; settings: AgentSettings };
  };
  "GET /api/settings": { res: AgentSettings };
  /** Every field but `onboarded`, `learnedProactiveCap` and `avatar` (set by the avatar routes below). */
  "PATCH /api/settings": { body: Partial<Omit<AgentSettings, "avatar">>; res: AgentSettings };

  // ── « Ton agent » (one agent per account today: agentId "main") ──
  /** 2–4 avatar candidates (Workers AI, counted in the AI budget; 10 series an hour); file keys under the agent's prefix. */
  "POST /api/agents/:agentId/avatar/candidates": { body: { style: AvatarStyle; prompt: string; count?: number }; res: { candidates: string[]; costUsd: number } };
  /** One of the last candidates becomes the avatar. */
  "PUT /api/agents/:agentId/avatar": { body: { fileKey: string }; res: AgentSettings };
  /** An image of the user's, base64 (PNG, JPEG or WebP, ≤ 2 MB, 64–4,096 px a side; the app sends it as 512 × 512). */
  "POST /api/agents/:agentId/avatar/upload": { body: { image: string }; res: AgentSettings };
  /** Back to the placeholder drawn from the name. */
  "DELETE /api/agents/:agentId/avatar": { res: AgentSettings };

  // ── « Mes agents » (several agents per account; botId "main" = the main agent; shared/bots.ts) ──
  "GET /api/bots": { res: { bots: BotView[]; max: number } };
  "POST /api/bots": { body: CreateBotInput; res: BotView };
  /** Conversations between the account's agents, newest first. */
  "GET /api/bots/threads": { res: { threads: BotThreadView[] } };
  "GET /api/bots/:botId": { res: BotView };
  "PATCH /api/bots/:botId": { body: BotPatch; res: BotView };
  /** An extra agent and its cloud computer (the shared memory stays). The main agent cannot be deleted. */
  "DELETE /api/bots/:botId": { res: { ok: true } };
  "GET /api/bots/:botId/chat": { query: { before?: string; limit?: string }; res: { messages: ChatMessage[] } };
  "POST /api/bots/:botId/chat": { body: { text: string; clientId: string; replyTo?: string; files?: string[] }; res: { accepted: boolean; turnId: string | null } };
  /** The agent's own cloud computer, created when missing (then managed with /api/machines/:id). */
  "POST /api/bots/:botId/computer": { body: { size?: MachineSize }; res: MachineView };

  // ── Chat (realtime events arrive on the WebSocket GET /api/agent) ──
  "GET /api/chat": { query: { before?: string; limit?: string }; res: { messages: ChatMessage[] } };
  "POST /api/chat": { body: { text: string; clientId: string; replyTo?: string; files?: string[] }; res: { accepted: boolean; turnId: string | null } };
  /** The owner's tapback on one of the agent's lines (`remove` takes it back): the reaction line, never answered. */
  "POST /api/chat/:id/react": { body: { emoji: (typeof import("./contracts").WEB_REACTIONS)[number]; remove?: boolean }; res: { message: ChatMessage } };

  // A 60-second Realtime client secret; the root OpenAI key stays on the Worker. Delegation keeps the usual approvals.
  "POST /api/voice/session": { body: { botId: string }; res: { token: string; model: string } };
  "POST /api/voice/delegate": { body: { botId: string; text: string; clientId: string }; res: { accepted: boolean; turnId: string | null } };
  "GET /api/voice/task": { query: { botId: string; turnId: string }; res: { turnId: string; status: TurnView["status"]; text: string } };

  // ── Pulse ──
  "GET /api/dashboard": { res: Dashboard };
  "GET /api/pulse/summary": { res: import("./pulse-summary").PulseSummary };
  "GET /api/briefs": { res: { briefs: Brief[] } };
  "GET /api/activity": { res: { turns: TurnView[]; usage: UsageView } };
  "GET /api/usage/breakdown": { res: import("./usage").UsageBreakdown };
  "GET /api/subagents": { res: { fanouts: import("./subagents").FanoutView[] } };

  // ── Approvals ──
  "GET /api/approvals": { query: { status?: string }; res: { approvals: ApprovalView[] } };
  "POST /api/approvals/:id/decide": { body: { decision: "approve" | "reject"; editedArgs?: Record<string, unknown>; note?: string; always?: boolean }; res: ApprovalView };

  // ── Rules (allow | ask | deny) ──
  "GET /api/rules": { res: { rules: RuleView[] } };
  "POST /api/rules": { body: RuleInput; res: RuleView };
  "DELETE /api/rules/:id": { res: { ok: true } };

  // ── Missions & runs ──
  "GET /api/missions": { res: { missions: MissionView[] } };
  "POST /api/missions": { body: CreateMissionInput; res: MissionView };
  "PATCH /api/missions/:id": { body: Partial<Pick<MissionView, "title" | "goal" | "cadenceMinutes" | "machine" | "status" | "notes">>; res: MissionView };
  "POST /api/missions/:id/run": { body: Record<string, never>; res: MissionRunView };
  "GET /api/runs": { query: { limit?: string }; res: { runs: MissionRunView[] } };
  "GET /api/runs/:id": { res: RunDetail };
  "POST /api/runs/:id/cancel": { body: Record<string, never>; res: MissionRunView };
  "POST /api/runs/:id/answer": { body: { answer: string; questionId?: string }; res: MissionRunView };

  // ── Veilles (watches): every route answers with the whole list ──
  "GET /api/watches": { res: WatchesView };
  "POST /api/watches": { body: WatchCreateInput; res: WatchesView };
  "PATCH /api/watches/:id": { body: WatchPatchInput; res: WatchesView };
  "DELETE /api/watches/:id": { res: WatchesView };
  /** « Plus de ça » (more: true) / « Moins de ça » on one item a watch found: feeds the relevance learner. */
  "POST /api/watches/items/:id/feedback": { body: { more: boolean }; res: WatchesView };

  // ── Files (screenshots, attachments) — binary response; key must belong to the user (prefix u/<userId>/) ──
  "GET /api/files/*": { res: Blob };
  /** Byte-checked upload, ≤10 MiB, five files / 20 MiB per chat; returned key belongs to this account only. */
  "POST /api/files": { body: { id: string; name: string; data: string }; res: { file: ChatFile } };
  /**
   * PUBLIC (no cookie read or set): a file the agent sent, for Sendblue to download as `media_url` (iMessage/SMS
   * only). The token is opaque (sealed), valid 24 h and bound to one file and one name; every failure is the same 404.
   */
  "GET /api/f/:token/:name": { res: Blob };

  // ── Memory ──
  "GET /api/memory": { query: { kind?: MemoryKind; status?: string; q?: string; cursor?: string }; res: { items: MemoryItemView[]; cursor: string | null } };
  "POST /api/memory": { body: { content: string; kind: MemoryKind; importance?: number; pinned?: boolean; dueAt?: number; sensitivity?: MemorySensitivity }; res: MemoryItemView };
  "PATCH /api/memory/:id": { body: { content?: string; importance?: number; pinned?: boolean; dueAt?: number | null; kind?: MemoryKind; sensitivity?: MemorySensitivity }; res: MemoryItemView };
  "DELETE /api/memory/:id": { query: { hard?: "1"; reason?: MemoryForgetReason }; res: { ok: true } };
  /** "Pourquoi tu sais ça ?" — sources and history of a memory. */
  "GET /api/memory/:id/why": { res: MemoryExplanation };
  "GET /api/memory/profile": { res: { blocks: ProfileBlockView[] } };
  "GET /api/memory/search": { query: { q: string }; res: RecallResult };
  "GET /api/memory/sleep": { res: MemorySleepReport };
  "POST /api/memory/:id/restore": { res: MemoryItemView };
  // ── Vault (« Coffre »): labels and field kinds only, never a value ──
  "GET /api/vault": { res: { items: VaultItemView[] } };
  "DELETE /api/vault/:n": { res: { ok: true } };
  /** The « Ajouter » form: values sent once, sealed at once, never answered back. */
  "POST /api/vault": { body: { kind: FormKind; label?: string | null; website?: string | null; fields: Partial<Record<FieldKind, string>> }; res: { ok: true; n: number; replaced: boolean } };
  /** A password manager's CSV export (1Password, Bitwarden, Chrome…), parsed server side. */
  "POST /api/vault/import": { body: { csv: string }; res: { ok: true; added: number; duplicates: number; skipped: number; format: string } };
  "GET /api/vault/:n/historique": { res: { item: VaultItemView; uses: VaultUseView[] } };
  /** « Révéler »: until when THIS session may reveal (null: a re-authentication first). */
  "GET /api/vault/reauth": { res: { until: number | null } };
  "POST /api/vault/reauth/start": { body: Record<string, never>; res: { ok: true; channel: "email" | "sms"; masked: string } };
  "POST /api/vault/reauth/verify": { body: { code: string }; res: { ok: true; until: number } };
  "POST /api/vault/:n/reveler": { body: Record<string, never>; res: { fields: { kind: FieldKind; label: string; value: string }[] } };
  /** PUBLIC — the one-tap add link the agent sends (write-only, once, 30 minutes). */
  "POST /api/coffre-lien/info": { body: { jeton: string }; res: { kind: FormKind; site: string | null; label: string | null; expiresAt: number } };
  "POST /api/coffre-lien": { body: { jeton: string; item: { kind: FormKind; label?: string | null; website?: string | null; fields: Partial<Record<FieldKind, string>> } }; res: { ok: true; n: number } };
  /** Imports memories from a previous system (batches of ≤ 1,000 records and ≤ 8 MB; re-embedded server-side). */
  "POST /api/memory/import": { body: { records: { content: string; kind?: MemoryKind; createdAt?: number; source?: string }[] }; res: { imported: number } };

  // ── Machines ──
  "GET /api/machines": { res: { machines: MachineView[] } };
  "POST /api/machines/cloud": { body: { size?: MachineSize; name?: string }; res: MachineView };
  "POST /api/machines/:id/start": { body: Record<string, never>; res: MachineView };
  "POST /api/machines/:id/stop": { body: Record<string, never>; res: MachineView };
  /** Cloud computer: wakes it ahead of its probable use (never waits: thawed or booted in the background). */
  "POST /api/machines/:id/wake": { body: { reason?: "page" | "run_page" | "screen" }; res: MachineView };
  /** Cloud computer: the owner takes the controls of the screen (`take: true`) or gives them back to the agent. */
  "POST /api/machines/:id/control": { body: { take: boolean }; res: MachineView };
  "PATCH /api/machines/:id": {
    body: { name?: string; size?: MachineSize; idleStopMinutes?: number; sleepStop?: "auto" | number; policy?: MachinePolicy; autoscale?: { maxSize?: MachineSizeName; pinned?: boolean } };
    res: MachineView;
  };
  "DELETE /api/machines/:id": { res: { ok: true } };
  /** WebSocket (noVNC) — cloud machines only, session + ownership checked. */
  "GET /api/machines/:id/vnc": { query: { desktop?: string }; res: never };
  "GET /api/machines/:id/desktops": { res: import("./desktops").DesktopsView };
  /** Pairing approval page data (session). */
  /** Pairing approval page data (session). Shows where the request came from so the user can spot a phishing link. */
  "GET /api/machines/pair/:userCode": {
    res: { userCode: string; name: string; os: string | null; arch: string | null; status: string; expiresAt: number; requestedAt: number; requestIp: string | null; requestCountry: string | null };
  };
  /** `typedCode` must be the code the user TYPED from the computer's screen (the URL prefill alone never approves). */
  "POST /api/machines/pair/approve": { body: { userCode: string; typedCode: string; name?: string; policy: MachinePolicy }; res: MachineView };
  "POST /api/machines/pair/deny": { body: { userCode: string }; res: { ok: true } };
  // DAEMON endpoints (no session; rate-limited; RFC 8628 shapes):
  "POST /api/machines/pair/start": {
    body: { name: string; os?: string; arch?: string; version?: string };
    res: { device_code: string; user_code: string; verification_uri: string; verification_uri_complete: string; expires_in: number; interval: number };
  };
  "POST /api/machines/pair/token": {
    body: { device_code: string; grant_type: "urn:ietf:params:oauth:grant-type:device_code" };
    res:
      | { machine_id: string; refresh_token: string; account: { agentName: string; email: string | null } }
      | { error: "authorization_pending" | "slow_down" | "access_denied" | "expired_token" };
  };
  /** Authorization: Bearer <refresh_token> → rotated refresh + 10-minute access token. */
  "POST /api/machines/token": { body: Record<string, never>; res: { access_token: string; refresh_token: string; machine_id: string; expires_in: number } };
  /** WebSocket, Authorization: Bearer <access_token>. */
  "GET /api/machines/connect": { res: never };

  // ── Connections ──
  "GET /api/connections": { res: ConnectionsView };
  "POST /api/connections/imessage/code": { body: Record<string, never>; res: { code: string; number: string; smsUri: string; expiresAt: number } };
  "DELETE /api/connections/imessage": { res: { ok: true } };
  "POST /api/connections/telegram/link": { body: Record<string, never>; res: { url: string; expiresAt: number } };
  "DELETE /api/connections/telegram": { res: { ok: true } };
  "GET /api/apps": { res: { available: boolean; toolkits: { slug: string; name: string; connected: boolean; status: string | null }[] } };
  "POST /api/apps/:toolkit/link": { body: Record<string, never>; res: { redirectUrl: string } };
  /** Owner only: creates (once) the Composio project webhook subscription pointing at /webhooks/composio. */
  "POST /api/apps/admin/subscribe": {
    body: Record<string, never>;
    res: { ok: true; created: boolean; updated: boolean; id: string; webhookUrl: string; enabledEvents: string[]; version: string | null; pointsHere: boolean; secretStored: boolean };
  };
  /** Owner only: registers the Telegram bot webhook (with its secret token) on this deployment's URL. */
  "POST /api/connections/telegram/webhook": { body: Record<string, never>; res: { ok: true; url: string } };
  /**
   * iCloud (Calendrier et Mail) with an app-specific password: tested live with Apple before anything is stored (the
   * password is never returned). Rate limited with PATCH (5 saves per hour).
   */
  "POST /api/icloud": { body: IcloudConnectInput; res: IcloudView };
  /** Switches a service on (live test with the stored password) or off, or changes the iCloud Mail address. */
  "PATCH /api/icloud": { body: { mail?: boolean; calendar?: boolean; mailAddress?: string | null }; res: IcloudView };
  /** Forgets the password and stops reading iCloud (the user still revokes it at Apple). */
  "DELETE /api/icloud": { res: { ok: true } };
  "GET /api/push/key": { res: { publicKey: string | null } };
  "POST /api/push/subscription": { body: { endpoint: string; keys: { p256dh: string; auth: string } }; res: { ok: true } };
  "DELETE /api/push/subscription": { body: { endpoint: string }; res: { ok: true } };

  // ── Health (PUBLIC) ──
  "GET /api/health": { res: { ok: true; version: string } };

  // ── End-to-end only (E2E === "1"; 404 otherwise) ──
  /** Without kind: every message but attachments; "email": e-mails only; "attachment": their attachments (subject = file name, body = JSON {type, size, sha256}). */
  "GET /__e2e/outbox": {
    query: { to: string; kind?: "email" | "attachment" };
    res: { messages: { kind: string; recipient: string; subject: string | null; body: string; createdAt: number }[] };
  };
  "GET /__e2e/user": { query: { email?: string; phone?: string }; res: { id: string } };
  "POST /__e2e/fire": { body: { userId: string; callback: import("./contracts").E2eCallback; payload?: unknown; botId?: string }; res: { result: unknown } };
  "POST /__e2e/memory-flush": { body: { userId: string }; res: { extracted: number } };
  "POST /__e2e/memory": { body: { userId: string } & import("./contracts").MemoryE2eOp; res: { result: unknown } };
  "POST /__e2e/clock": { body: { userId: string; offsetMs: number; botId?: string }; res: { ok: true } };
  "GET /__e2e/inspect": { query: { userId: string; what: "schedules" | "turns" | "outbox" | "signals" | "proactive" | "icloud" | "prewarm" | "approvals" | "vault" | "vaultExport" | "wakes" | "images" | "outboundFiles"; botId?: string }; res: { result: unknown } };
  /** Every boot of one of the user's computers (MachineDO audit « machine.start »), oldest first. */
  "GET /__e2e/machine-boots": { query: { userId: string; machineId: string }; res: { boots: { at: number; ok: boolean; summary: string }[] } };
  /**
   * The next `starts` starts of a cloud computer fail like the platform's (after start(), before the instance answers);
   * the next `largeRestores` transfers of its large home folders fail (two-step restore).
   */
  "POST /__e2e/machine-faults": { body: { userId: string; machineId: string; starts?: number; largeRestores?: number }; res: { ok: true } };
  /**
   * One tick of a cloud computer after ageing its activity (`idleMs`) or its sleep in progress (`sleptMs`): the light
   * sleep and the full stop without waiting real minutes; `leaseAgeMs` ages every desktop lease (a run that waited
   * hours lost its desktop). Returns the view, the container's sleep state and the timings.
   */
  "POST /__e2e/machine-tick": {
    body: { userId: string; machineId: string; idleMs?: number; sleptMs?: number; leaseAgeMs?: number };
    res: {
      view: MachineView;
      sleep: { state: string; frozen: number } | null;
      timings: Record<string, unknown>[];
      audit: { at: number; method: string; summary: string; ok: boolean }[];
    };
  };
  /** Sets the user's spend for the current month (budget-mode tests). */
  "POST /__e2e/usage": { body: { userId: string; usd: number }; res: { ok: true } };
  "POST /__e2e/ledger": { body: { userId: string; usd: number; category?: string }; res: { ok: true } };
  "GET /__e2e/ledger": {
    query: { userId: string };
    res: {
      rows: {
        day: string;
        category: string;
        model: string;
        calls: number;
        input_tokens: number;
        cache_write_tokens: number;
        cache_read_tokens: number;
        output_tokens: number;
        cost_micros: number;
      }[];
    };
  };
}

/** Toolkits offered in the Connexions page's apps grid (Composio slugs: GET /api/apps, POST /api/apps/:toolkit/link). */
export const APP_TOOLKITS: { slug: string; name: string }[] = [
  { slug: "gmail", name: "Gmail" },
  { slug: "googlecalendar", name: "Google Agenda" },
  { slug: "googledrive", name: "Google Drive" },
  { slug: "slack", name: "Slack" },
  { slug: "notion", name: "Notion" },
  { slug: "github", name: "GitHub" },
  { slug: "outlook", name: "Outlook" },
  { slug: "linear", name: "Linear" },
];

/**
 * Apps Pulsez connects natively (not through Composio): they have their own card and routes, but are watched,
 * named and listed among the connected apps like the toolkits above.
 */
export const NATIVE_APP_TOOLKITS: { slug: string; name: string }[] = [
  { slug: ICLOUD_SERVICES.mail.toolkit, name: ICLOUD_SERVICES.mail.name },
  { slug: ICLOUD_SERVICES.calendar.toolkit, name: ICLOUD_SERVICES.calendar.name },
];

/** Every app that can be connected and watched (« surveille <App> »), Composio or native. */
export const ALL_APP_TOOLKITS: { slug: string; name: string }[] = [...APP_TOOLKITS, ...NATIVE_APP_TOOLKITS];

/** Calendar and mail toolkits (generic « mon agenda » / « mes mails », urgency, claims, the brief's reads). */
export const CALENDAR_TOOLKITS: readonly string[] = ["googlecalendar", "icloudcalendar"];
export const MAIL_TOOLKITS: readonly string[] = ["gmail", "outlook", "icloudmail"];
